import { useState, useEffect, useCallback } from "react";
import students from "../../services/students";
import { Student, User, Avaliation } from "../../types";
import { useAuth } from "../../context/AuthContext";
import NewStudentModal from "../../components/students/modals/NewStudentModal";
import StudentManagementModal from "../../components/students/StudentManagementModal/StudentManagementModal";
import BodyAvaliationModal from "../../components/avaliations/BodyAvaliationModal";
import AvaliationDateSelectorModal from "../../components/avaliations/AvaliationDateSelectorModal";
import avaliationsService from "../../services/avaliations";
import {
  calculateIMC,
  calculatePercentualGordura,
  calculatePesoGordo,
  calculatePesoMagro,
  calculateDistribuicaoDobras,
} from "../../helpers/avaliationCalculations";
import StudentsHeader from "../../components/students/list/StudentsHeader";
import StudentsStatsCards from "../../components/students/list/StudentsStatsCards";
import StudentsFilters from "../../components/students/list/StudentsFilters";
import StudentCard from "../../components/students/list/StudentCard";
import StudentsEmptyState from "../../components/students/list/StudentsEmptyState";
import StudentsLoadingSkeleton from "../../components/students/list/StudentsLoadingSkeleton";
import NotificationModal from "../../components/students/modals/NotificationModal";

const STORAGE_KEY = "students_sort_option";

function Students() {
  const { getUser } = useAuth();
  const [studentsList, setStudentsList] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [genderFilter, setGenderFilter] = useState("");

  // Recuperar a ordenação do localStorage
  const getInitialSortOption = ():
    | "name_asc"
    | "name_desc"
    | "validity_desc"
    | "validity_asc"
    | "workouts_count_desc"
    | "workouts_count_asc"
    | "appointments_count_desc"
    | "appointments_count_asc"
    | "foods_count_desc"
    | "foods_count_asc"
    | "avaliations_count_desc"
    | "avaliations_count_asc"
    | "birth_date_asc"
    | "birth_date_desc" => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (
        saved &&
        [
          "name_asc",
          "name_desc",
          "validity_desc",
          "validity_asc",
          "workouts_count_desc",
          "workouts_count_asc",
          "appointments_count_desc",
          "appointments_count_asc",
          "foods_count_desc",
          "foods_count_asc",
          "avaliations_count_desc",
          "avaliations_count_asc",
          "birth_date_asc",
          "birth_date_desc",
        ].includes(saved)
      ) {
        return saved as
          | "name_asc"
          | "name_desc"
          | "validity_desc"
          | "validity_asc"
          | "workouts_count_desc"
          | "workouts_count_asc"
          | "appointments_count_desc"
          | "appointments_count_asc"
          | "foods_count_desc"
          | "foods_count_asc"
          | "avaliations_count_desc"
          | "avaliations_count_asc"
          | "birth_date_asc"
          | "birth_date_desc";
      }
    } catch (error) {
      console.error("Erro ao recuperar ordenação do localStorage:", error);
    }
    return "name_asc";
  };

  const [sortOption, setSortOption] = useState<
    | "name_asc"
    | "name_desc"
    | "validity_desc"
    | "validity_asc"
    | "workouts_count_desc"
    | "workouts_count_asc"
    | "appointments_count_desc"
    | "appointments_count_asc"
    | "foods_count_desc"
    | "foods_count_asc"
    | "avaliations_count_desc"
    | "avaliations_count_asc"
    | "birth_date_asc"
    | "birth_date_desc"
  >(getInitialSortOption);
  const [newStudentModalOpen, setNewStudentModalOpen] = useState(false);
  const [selectedStudentForManagement, setSelectedStudentForManagement] =
    useState<User | null>(null);
  const [
    selectedStudentForBodyAvaliation,
    setSelectedStudentForBodyAvaliation,
  ] = useState<Student | null>(null);
  const [avaliationDates, setAvaliationDates] = useState<string[]>([]);
  const [selectedAvaliationDate, setSelectedAvaliationDate] = useState<
    string | null
  >(null);
  const [selectedAvaliation, setSelectedAvaliation] =
    useState<Avaliation | null>(null);
  const [dateSelectorOpen, setDateSelectorOpen] = useState(false);
  const [selectedStudentForNotification, setSelectedStudentForNotification] =
    useState<Student | null>(null);
  const [notificationAllOpen, setNotificationAllOpen] = useState(false);

  const loadStudents = useCallback(async () => {
    const user = getUser();
    const professionalId = user?.id ? String(user.id) : "";

    if (!professionalId) return;

    setLoading(true);
    setLoadError(null);
    try {
      const data = await students.listAll(professionalId);
      if (data?.students) {
        setStudentsList(data.students);
        setFilteredStudents(data.students);
      }
    } catch (error) {
      console.error("Erro ao carregar alunos:", error);
      setLoadError("Não foi possível carregar os alunos. Tente novamente.");
      setStudentsList([]);
      setFilteredStudents([]);
    } finally {
      setLoading(false);
    }
  }, [getUser]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  // Salvar a ordenação no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, sortOption);
    } catch (error) {
      console.error("Erro ao salvar ordenação no localStorage:", error);
    }
  }, [sortOption]);

  const openBodyAvaliation = async (student: Student) => {
    const user = getUser();
    const professionalId = user?.id ? String(user.id) : "";
    if (!professionalId) return;

    const list = await avaliationsService.listall(
      professionalId,
      String(student.user.id),
    );

    if (!list || list.length === 0) {
      setSelectedStudentForBodyAvaliation(student);
      setAvaliationDates([]);
      setSelectedAvaliation(null);
      setSelectedAvaliationDate(null);
      setDateSelectorOpen(true);
      return;
    }

    const dates = list
      .map((av: Avaliation) => av.date || av.registe || av.created_at)
      .filter(Boolean) as string[];

    const sorted = [...dates].sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    );

    const lastDate = sorted[0];
    const lastAvaliation =
      list.find(
        (av: Avaliation) =>
          (av.date || av.registe || av.created_at) &&
          (av.date || av.registe || av.created_at) === lastDate,
      ) || list[list.length - 1];

    setSelectedStudentForBodyAvaliation(student);
    setAvaliationDates(sorted);
    setSelectedAvaliationDate(lastDate);
    setSelectedAvaliation(lastAvaliation);
  };

  useEffect(() => {
    const term = search.trim().toLowerCase();

    let filtered = studentsList;

    if (term !== "") {
      filtered = filtered.filter((student) =>
        student.user?.name?.toLowerCase().includes(term),
      );
    }

    if (genderFilter !== "") {
      filtered = filtered.filter(
        (student) => student.user?.gener === genderFilter,
      );
    }

    const sorted = [...filtered].sort((a, b) => {
      if (sortOption === "name_asc" || sortOption === "name_desc") {
        const nameA = a.user?.name?.toLowerCase() || "";
        const nameB = b.user?.name?.toLowerCase() || "";
        if (nameA < nameB) return sortOption === "name_asc" ? -1 : 1;
        if (nameA > nameB) return sortOption === "name_asc" ? 1 : -1;
        return 0;
      }

      if (
        sortOption === "workouts_count_asc" ||
        sortOption === "workouts_count_desc"
      ) {
        const countA = a.workouts_count || 0;
        const countB = b.workouts_count || 0;
        return sortOption === "workouts_count_asc"
          ? countA - countB
          : countB - countA;
      }

      if (
        sortOption === "appointments_count_asc" ||
        sortOption === "appointments_count_desc"
      ) {
        const countA = a.appointments_count || 0;
        const countB = b.appointments_count || 0;
        return sortOption === "appointments_count_asc"
          ? countA - countB
          : countB - countA;
      }

      if (
        sortOption === "foods_count_asc" ||
        sortOption === "foods_count_desc"
      ) {
        const countA = a.foods_count || 0;
        const countB = b.foods_count || 0;
        return sortOption === "foods_count_asc"
          ? countA - countB
          : countB - countA;
      }

      if (
        sortOption === "avaliations_count_asc" ||
        sortOption === "avaliations_count_desc"
      ) {
        const countA = a.avaliations_count || 0;
        const countB = b.avaliations_count || 0;
        return sortOption === "avaliations_count_asc"
          ? countA - countB
          : countB - countA;
      }

      if (sortOption === "birth_date_asc" || sortOption === "birth_date_desc") {
        const getDayMonth = (dateStr?: string) => {
          if (!dateStr) return 0;
          // Assumes YYYY-MM-DD format from backend
          const parts = dateStr.split("-");
          if (parts.length === 3) {
            const month = parseInt(parts[1], 10);
            const day = parseInt(parts[2], 10);
            return month * 100 + day;
          }
          return 0;
        };

        const dateA = getDayMonth(a.user?.birth_date);
        const dateB = getDayMonth(b.user?.birth_date);

        return sortOption === "birth_date_asc" ? dateA - dateB : dateB - dateA;
      }

      const validityA = a.validity ? new Date(a.validity).getTime() : 0;
      const validityB = b.validity ? new Date(b.validity).getTime() : 0;

      if (validityA === validityB) return 0;

      if (sortOption === "validity_desc") {
        return validityB - validityA;
      }

      return validityA - validityB;
    });

    setFilteredStudents(sorted);
  }, [search, studentsList, sortOption, genderFilter]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const isValidityExpired = (validity: string) => {
    if (!validity) return false;
    return new Date(validity) < new Date();
  };

  const getValidityStatus = (validity: string) => {
    if (!validity) return { color: "gray", label: "Sem validade" };

    const validityDate = new Date(validity);
    const today = new Date();
    const diffDays = Math.ceil(
      (validityDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffDays < 0 || diffDays === 0)
      return { color: "red", label: "Vencido" };
    if (diffDays <= 7) return { color: "yellow", label: `${diffDays} dias` };
    if (diffDays <= 30) return { color: "orange", label: `${diffDays} dias` };
    return { color: "green", label: "Ativo" };
  };

  const renderStudentsContent = () => {
    if (loading) {
      return <StudentsLoadingSkeleton />;
    }

    if (loadError) {
      return (
        <div className="flex min-h-[320px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-red-200 bg-red-50 px-6 py-10 text-center dark:border-red-900/40 dark:bg-red-950/20">
          <p className="text-sm font-medium text-red-700 dark:text-red-300">
            {loadError}
          </p>
          <button
            type="button"
            onClick={() => void loadStudents()}
            className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white"
          >
            Tentar novamente
          </button>
        </div>
      );
    }

    if (filteredStudents.length > 0) {
      return (
        <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 md:gap-5 xl:grid-cols-3">
          {filteredStudents.map((student: Student) => {
            const validityStatus = getValidityStatus(student.validity);

            return (
              <StudentCard
                key={student.id}
                student={student}
                validityStatus={validityStatus}
                onManageClick={() =>
                  setSelectedStudentForManagement({
                    ...student.user,
                    user_validity: student.validity,
                  })
                }
                onAvaliationClick={() => openBodyAvaliation(student)}
                onNotificationClick={() => {
                  setSelectedStudentForNotification(student);
                }}
              />
            );
          })}
        </div>
      );
    }

    return <StudentsEmptyState hasSearch={!!search} />;
  };

  return (
    <div className="mx-auto max-w-7xl px-3 py-4 sm:px-4 sm:py-5 md:px-6 md:py-6 2xl:px-10 pb-20 md:pb-6">
      {/* Header */}
      <StudentsHeader
        onNewStudentClick={() => setNewStudentModalOpen(true)}
        onNotificationAllClick={() => setNotificationAllOpen(true)}
      />

      {/* Stats Cards */}
      <StudentsStatsCards
        students={studentsList}
        isValidityExpired={isValidityExpired}
      />

      {/* Card Principal - Mobile First */}
      <div className="rounded-xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark sm:rounded-2xl overflow-hidden">
        {/* Header com Filtros */}
        <StudentsFilters
          search={search}
          onSearchChange={handleSearchChange}
          sortOption={sortOption}
          onSortChange={setSortOption}
          filteredCount={filteredStudents.length}
          genderFilter={genderFilter}
          onGenderChange={setGenderFilter}
        />

        {/* Conteúdo */}
        <div className="p-3 sm:p-4 sm:px-6 sm:py-5 md:px-10 md:py-7">
          {renderStudentsContent()}
        </div>
      </div>

      {/* Modals */}
      <NewStudentModal
        isOpen={newStudentModalOpen}
        onClose={() => setNewStudentModalOpen(false)}
        onSuccess={() => {
          setNewStudentModalOpen(false);
          loadStudents();
        }}
      />

      <StudentManagementModal
        isOpen={!!selectedStudentForManagement}
        onClose={() => setSelectedStudentForManagement(null)}
        student={selectedStudentForManagement}
      />

      {selectedStudentForBodyAvaliation && selectedAvaliation && (
        <BodyAvaliationModal
          isOpen={!!selectedStudentForBodyAvaliation && !!selectedAvaliation}
          onClose={() => {
            setSelectedStudentForBodyAvaliation(null);
            setSelectedAvaliation(null);
          }}
          studentName={selectedStudentForBodyAvaliation.user.name}
          professionalName={getUser()?.name || ""}
          data={selectedAvaliation}
          calculated={(() => {
            const aval = selectedAvaliation as Avaliation;
            const gender =
              selectedStudentForBodyAvaliation.user.gener === "F" ? "F" : "M";
            const percentualGordura = calculatePercentualGordura(aval, gender);
            const imc = calculateIMC(aval);
            const pesoMagro = calculatePesoMagro(aval, percentualGordura);
            const pesoGordo = calculatePesoGordo(aval, pesoMagro);
            const dist = calculateDistribuicaoDobras(aval);
            return {
              percentualGordura,
              imc,
              pesoMagro,
              pesoGordo,
              distribuicaoMMSS: dist.distribuicaoMMSS,
              distribuicaoMMII: dist.distribuicaoMMII,
              distribuicaoTronco: dist.distribuicaoTronco,
              somatorioPerimetros: null,
            };
          })()}
          date={selectedAvaliationDate}
        />
      )}

      <AvaliationDateSelectorModal
        isOpen={dateSelectorOpen}
        dates={avaliationDates}
        selectedDate={selectedAvaliationDate}
        onChange={(value) => {
          setSelectedAvaliationDate(value);
          setDateSelectorOpen(false);
        }}
        onClose={() => setDateSelectorOpen(false)}
      />

      <NotificationModal
        isOpen={!!selectedStudentForNotification}
        onClose={() => setSelectedStudentForNotification(null)}
        student={selectedStudentForNotification}
        onSuccess={() => {
          // Opcional: recarregar dados se necessário
        }}
      />

      <NotificationModal
        isOpen={notificationAllOpen}
        onClose={() => setNotificationAllOpen(false)}
        student={null}
        sendToAll={true}
        onSuccess={() => {
          // Opcional: recarregar dados se necessário
        }}
      />
    </div>
  );
}

export default Students;
